import type { DependencyList, RefObject } from 'react';
import type { HotkeyCallback, Keys, Options } from 'react-hotkeys-hook';
// eslint-disable-next-line no-restricted-imports -- sole sanctioned import; all app code uses useClarionHotkeys
import { useHotkeys } from 'react-hotkeys-hook';
import type { Trigger } from 'react-hotkeys-hook/dist/types';
import { usePref } from './prefs';

/** @returns whether the user has keyboard shortcuts enabled */
export function useKeyboardShortcutsEnabled(): boolean {
  const [enabled] = usePref('keyboard.shortcutsEnabled', true);
  return enabled;
}

/**
 * Wraps `useHotkeys` from `react-hotkeys-hook`, but respects the `keyboard.shortcutsEnabled` preference.
 */
export function useClarionHotkeys<T extends HTMLElement = HTMLElement>(
  keys: Keys,
  callback: HotkeyCallback,
  options: Options & { description: string }, // Make description required for hotkeys dialog
  deps?: DependencyList,
): RefObject<T | null> {
  const shortcutsEnabled = useKeyboardShortcutsEnabled();
  const mergedOptions = withEnabled(shortcutsEnabled, options);
  return useHotkeys(keys, callback, mergedOptions, deps ?? []) as RefObject<T | null>;
}

function mergeEnabled(shortcutsEnabled: boolean, consumerEnabled: Trigger | undefined): Trigger {
  if (!shortcutsEnabled) return false;
  return consumerEnabled ?? true;
}

function withEnabled(shortcutsEnabled: boolean, options: Options): Options {
  return { ...options, enabled: mergeEnabled(shortcutsEnabled, options.enabled) };
}
